import { useState, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import CameraCapture from '../components/CameraCapture';
import ContractDocument from '../components/ContractDocument';
import { buildContractContext } from '../utils/contractContext';
import { printContract } from '../utils/renderContract';

// EndLeaseModal — 4-step end-of-lease wizard for long-term leases.
//   Step 1: Return Inspection (odometer, fuel, condition, photos)
//   Step 2: Charges (excess km auto-calc, damage, late fee, deposit handling)
//   Step 3: Optional Buy-out (placeholder — full impl next commit)
//   Step 4: Sign-off (final invoice + signature, move to rentalHistory)

const STEPS = ['Return Inspection', 'Charges', 'Buy-out', 'Sign-off'];

const COND_OPTIONS = [
  { value: 'no_damage',     label: '✅ Returned in good condition — fair wear & tear only' },
  { value: 'small_scratch', label: '🔸 Small scratches / scuffs beyond fair wear' },
  { value: 'damage',        label: '⚠️ Visible damage — chargeable' },
  { value: 'major_damage',  label: '🔴 Major damage / accident — insurance claim' },
];

const FUEL_OPTIONS = [
  { value: 'full',  label: '⛽ Full' },
  { value: '3-4',   label: '¾' },
  { value: 'half',  label: '½' },
  { value: '1-4',   label: '¼' },
  { value: 'empty', label: '🅴 Empty' },
];

const PHOTO_SLOTS = ['Front', 'Rear', 'Left side', 'Right side', 'Dashboard', 'Interior'];

const DEFAULT_EXCESS_KM_RATE = 0.15;   // €/km — typical Cyprus excess-km rate
const DEFAULT_LATE_FEE_PER_DAY = 50;   // €/day for unreturned vehicle past lease end

export default function EndLeaseModal() {
  const {
    modals, closeModal, openModal,
    rentals, vehicles, customers,
    endRental, addInvoice, nextInvoiceNum,
    companySettings, agreementTemplates,
    currentUserId, showToast,
    getDamagesForRental,
  } = useApp();

  const open      = modals['end-lease'];
  const targetVid = modals['end-lease-vid'];

  const lease    = targetVid ? rentals.find(r => r.vid === targetVid && r.type === 'lease' && ['active','overdue'].includes(r.status)) : null;
  const vehicle  = targetVid ? vehicles.find(v => v.id === targetVid) : null;

  // ── State ───────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  // Step 1
  const [returnKm, setReturnKm]   = useState('');
  const [fuel, setFuel]           = useState('full');
  const [cond, setCond]           = useState('no_damage');
  const [condNote, setCondNote]   = useState('');
  const [photos, setPhotos]       = useState({});
  const [activeSlot, setActiveSlot] = useState(null);
  const [camera, setCamera]       = useState(null);
  const slotFileRef = useRef(null);
  // Step 2
  const [excessKmRate, setExcessKmRate] = useState(DEFAULT_EXCESS_KM_RATE);
  const [damageFee, setDamageFee]       = useState('');
  const [damageNote, setDamageNote]     = useState('');
  const [lateFeePerDay, setLateFeePerDay] = useState(DEFAULT_LATE_FEE_PER_DAY);
  const [lateFeeOverride, setLateFeeOverride] = useState(''); // operator can override auto-calc
  const [depositRefund, setDepositRefund] = useState('auto'); // 'auto' = settle from balance
  const [depositPartialAmt, setDepositPartialAmt] = useState('');
  // Step 3
  const [buyout, setBuyout] = useState(false);
  const [buyoutPrice, setBuyoutPrice] = useState('');
  // Step 4
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [signedAt, setSignedAt] = useState('');
  const [showSig, setShowSig] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const sigCanvasRef = useRef(null);
  const drawingRef   = useRef(false);
  const [issueFinalInvoice, setIssueFinalInvoice] = useState(true);

  // ── Reset on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setReturnKm(vehicle?.odometer ? String(vehicle.odometer) : '');
    setFuel('full');
    setCond('no_damage');
    setCondNote('');
    setPhotos({});
    setActiveSlot(null);
    setCamera(null);
    setExcessKmRate(DEFAULT_EXCESS_KM_RATE);
    setDamageFee('');
    setDamageNote('');
    setLateFeePerDay(DEFAULT_LATE_FEE_PER_DAY);
    setLateFeeOverride('');
    setDepositRefund('auto');
    setDepositPartialAmt('');
    setBuyout(false);
    setBuyoutPrice('');
    setSignatureDataUrl('');
    setSignedAt('');
    setShowSig(false);
    setShowPreview(false);
    setIssueFinalInvoice(true);
  }, [open]); // eslint-disable-line

  // ── Photo slot helpers ─────────────────────────────────────────────
  function openSlotFile(label) { setActiveSlot(label); slotFileRef.current?.click(); }
  function onSlotFile(e) {
    const file = e.target.files?.[0];
    if (!file || !activeSlot) return;
    const reader = new FileReader();
    reader.onload = ev => setPhotos(prev => ({ ...prev, [activeSlot]: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  function openSlotCamera(label) {
    setCamera({
      label: `${label} — Return`,
      setPhoto: (data) => setPhotos(prev => ({ ...prev, [label]: data })),
    });
  }
  function removeSlotPhoto(label) {
    setPhotos(prev => { const n = { ...prev }; delete n[label]; return n; });
  }

  // ── Derived math (memoised — must run before any early return) ─────
  const calc = useMemo(() => {
    if (!lease) return null;
    const startKm = parseInt(lease.startKm) || parseInt(vehicle?.startOdometer) || 0;
    const retKm = parseInt(returnKm) || 0;
    const kmDriven = Math.max(0, retKm - startKm);

    // Months elapsed since lease start (rounded to 1 decimal for transparency)
    const startDt = new Date(lease.start);
    const today = new Date();
    const msPerMonth = 30.44 * 24 * 3600 * 1000;
    const monthsElapsed = Math.max(0, (today - startDt) / msPerMonth);
    const monthsElapsedRounded = Math.round(monthsElapsed * 10) / 10;

    const annualCap = parseInt(lease.annualMileageCap) || 0;
    const allowedKm = annualCap > 0
      ? Math.round(annualCap * (monthsElapsed / 12))
      : Infinity; // unlimited

    const excessKm = annualCap > 0 ? Math.max(0, kmDriven - allowedKm) : 0;
    const excessKmCharge = Math.round(excessKm * (parseFloat(excessKmRate) || 0) * 100) / 100;

    // Late fee — days past lease end
    const endDt = new Date(lease.end);
    const daysLate = Math.max(0, Math.ceil((today - endDt) / (24 * 3600 * 1000)));
    const autoLateFee = daysLate * (parseFloat(lateFeePerDay) || 0);
    const lateFeeAmt = lateFeeOverride !== '' ? (parseFloat(lateFeeOverride) || 0) : autoLateFee;

    const damageFeeAmt = parseFloat(damageFee) || 0;
    const buyoutAmt = buyout ? (parseFloat(buyoutPrice) || 0) : 0;

    const totalCharges = excessKmCharge + damageFeeAmt + lateFeeAmt + buyoutAmt;
    const deposit = parseFloat(lease.deposit) || 0;

    // Net settlement (positive = customer owes us, negative = we refund)
    const netOwedByCustomer = totalCharges - deposit;
    let depositReturned = 0;
    let depositDeducted = 0;
    let finalDue = 0;
    if (depositRefund === 'auto') {
      if (totalCharges >= deposit) {
        depositDeducted = deposit;
        finalDue = totalCharges - deposit;
        depositReturned = 0;
      } else {
        depositDeducted = totalCharges;
        depositReturned = deposit - totalCharges;
        finalDue = 0;
      }
    } else if (depositRefund === 'full') {
      depositReturned = deposit;
      depositDeducted = 0;
      finalDue = totalCharges;
    } else if (depositRefund === 'partial') {
      depositReturned = Math.min(deposit, parseFloat(depositPartialAmt) || 0);
      depositDeducted = Math.max(0, deposit - depositReturned);
      finalDue = Math.max(0, totalCharges - depositDeducted);
    } else if (depositRefund === 'none') {
      depositReturned = 0;
      depositDeducted = deposit;
      finalDue = Math.max(0, totalCharges - deposit);
    }

    return {
      startKm, retKm, kmDriven, monthsElapsed: monthsElapsedRounded,
      annualCap, allowedKm: annualCap > 0 ? allowedKm : null,
      excessKm, excessKmCharge,
      daysLate, autoLateFee, lateFeeAmt,
      damageFeeAmt, buyoutAmt,
      totalCharges, deposit,
      netOwedByCustomer,
      depositReturned, depositDeducted, finalDue,
    };
  }, [lease, vehicle, returnKm, excessKmRate, damageFee, lateFeePerDay, lateFeeOverride, depositRefund, depositPartialAmt, buyout, buyoutPrice]);

  // ── Contract context for sign-off (memoised) ───────────────────────
  function getCloseoutContext() {
    if (!lease) return { tmpl: null, ctx: null };
    const tmpl = (agreementTemplates || []).find(t => t.appliesTo === 'lease')
      || (agreementTemplates || []).find(t => t.isDefault)
      || (agreementTemplates || [])[0];
    const ctx = buildContractContext({
      rental: { ...lease, signatureDataUrl, signedAt: signedAt || lease.signedAt },
      vehicle, company: companySettings,
      signatureDataUrl, signedAt: signedAt || lease.signedAt,
    });
    return { tmpl, ctx };
  }

  // ── Submit ────────────────────────────────────────────────────────
  function submit() {
    if (!lease) return;
    if (!calc) return;

    // Persist closure data on the rental snapshot
    const closeoutData = {
      // Standard end-of-rental fields (shared schema with EndRentalModal)
      condRet:        cond,
      condRetNote:    condNote,
      returnKm:       calc.retKm || null,
      returnFuel:     fuel,
      extraFee:       calc.totalCharges,         // total chargeable
      extraNote:      [
        calc.excessKmCharge > 0 ? `Excess km: ${calc.excessKm} km × €${excessKmRate} = €${calc.excessKmCharge.toFixed(2)}` : '',
        calc.damageFeeAmt > 0   ? `Damage: €${calc.damageFeeAmt.toFixed(2)}${damageNote ? ` (${damageNote})` : ''}` : '',
        calc.lateFeeAmt > 0     ? `Late fee: €${calc.lateFeeAmt.toFixed(2)} (${calc.daysLate} day${calc.daysLate === 1 ? '' : 's'} late)` : '',
        calc.buyoutAmt > 0      ? `Buy-out: €${calc.buyoutAmt.toFixed(2)}` : '',
      ].filter(Boolean).join(' · '),
      depositRefund,
      depositReturned: calc.depositReturned,
      depositDeducted: calc.depositDeducted,
      returnedAt:     new Date().toISOString(),
      returnPhotos:   PHOTO_SLOTS.filter(l => photos[l]).map(l => photos[l]),
      // Lease-specific closeout snapshot
      leaseCloseout: {
        startKm:         calc.startKm,
        returnKm:        calc.retKm,
        kmDriven:        calc.kmDriven,
        monthsElapsed:   calc.monthsElapsed,
        annualMileageCap: calc.annualCap,
        allowedKm:       calc.allowedKm,
        excessKm:        calc.excessKm,
        excessKmRate:    parseFloat(excessKmRate) || 0,
        excessKmCharge:  calc.excessKmCharge,
        daysLate:        calc.daysLate,
        lateFeePerDay:   parseFloat(lateFeePerDay) || 0,
        lateFeeAmt:      calc.lateFeeAmt,
        damageFee:       calc.damageFeeAmt,
        damageNote,
        buyout,
        buyoutPrice:     calc.buyoutAmt,
        totalCharges:    calc.totalCharges,
        deposit:         calc.deposit,
        depositReturned: calc.depositReturned,
        depositDeducted: calc.depositDeducted,
        finalDue:        calc.finalDue,
        closedAt:        new Date().toISOString(),
        closedBy:        currentUserId,
        closeoutSignature: signatureDataUrl || null,
      },
      checkedOutBy: currentUserId,
      checkedOutAt: new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
      assignedTo: null,
      assignPhase: null,
    };

    // ── Auto-generate FINAL settlement invoice (if there are charges) ──
    if (issueFinalInvoice && calc.finalDue > 0) {
      const vatRate = parseFloat(lease.vatRate) || parseFloat(companySettings?.vatRate || 19);
      const vatMode = lease.vatMode || companySettings?.vatMode || 'inclusive';
      const gross = calc.finalDue;
      const subtotal = vatMode === 'inclusive' ? gross / (1 + vatRate / 100) : gross;
      const vatAmt   = vatMode === 'inclusive' ? gross - subtotal : gross * vatRate / 100;
      const total    = vatMode === 'inclusive' ? gross : gross + vatAmt;
      const now = new Date();
      const fmtShort = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const due = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 15);

      const lines = [];
      if (calc.excessKmCharge > 0) {
        lines.push({ desc: `Excess mileage — ${calc.excessKm} km × €${excessKmRate.toFixed ? excessKmRate.toFixed(2) : excessKmRate}/km`, qty: 1, unit: calc.excessKmCharge, total: calc.excessKmCharge });
      }
      if (calc.damageFeeAmt > 0) {
        lines.push({ desc: `Damage charge${damageNote ? ` — ${damageNote}` : ''}`, qty: 1, unit: calc.damageFeeAmt, total: calc.damageFeeAmt });
      }
      if (calc.lateFeeAmt > 0) {
        lines.push({ desc: `Late return fee — ${calc.daysLate} day${calc.daysLate === 1 ? '' : 's'} × €${lateFeePerDay}/day`, qty: 1, unit: calc.lateFeeAmt, total: calc.lateFeeAmt });
      }
      if (calc.buyoutAmt > 0) {
        lines.push({ desc: `Vehicle buy-out — ${vehicle?.name || ''}`, qty: 1, unit: calc.buyoutAmt, total: calc.buyoutAmt });
      }
      if (calc.depositDeducted > 0) {
        lines.push({ desc: `Less: deposit deduction`, qty: 1, unit: -calc.depositDeducted, total: -calc.depositDeducted });
      }

      const invNum = nextInvoiceNum();
      const finalInvoice = {
        id: 'inv-' + Date.now().toString(36),
        num: invNum, status: 'pending',
        issued: fmtShort(now), issuedAt: now.toISOString(),
        customer: lease.customer,
        customerCompany: lease.customerCompany || null,
        customerVatNumber: lease.vatNumber || null,
        customerId: lease.customerId,
        vehicle: vehicle?.name || '',
        vid: lease.vid,
        contract: lease.id,
        method: lease.payMethod || 'bank',
        methodLabel: 'Bank Transfer',
        issueDate: now.toISOString().slice(0, 10),
        dueDate:   due.toISOString().slice(0, 10),
        due:       fmtShort(due),
        lines,
        subtotal, vatRate, vatAmt, vatMode,
        discount: 0, discType: 'eur', total,
        notes: `Final settlement — Lease ${lease.id} closed ${fmtShort(now)}`,
        isLeaseInvoice: true,
        isFinalSettlement: true,
        email: lease.email || '',
      };
      addInvoice(finalInvoice);
    }

    // Refund-due note (when deposit > charges)
    if (calc.depositReturned > 0 && calc.finalDue === 0 && calc.deposit > 0) {
      // operator should process the refund manually — flag it for visibility
      closeoutData.depositRefundDueAmt = calc.depositReturned;
    }

    endRental(targetVid, closeoutData);
    closeModal('end-lease');

    const msg = calc.finalDue > 0
      ? `✓ Lease ${lease.id} closed — final pro forma €${calc.finalDue.toFixed(2)} issued`
      : calc.depositReturned > 0
        ? `✓ Lease ${lease.id} closed — €${calc.depositReturned.toFixed(2)} deposit refund due`
        : `✓ Lease ${lease.id} closed — settled in full`;
    showToast(msg);
  }

  if (!open) return null;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Modal open={!!open} size="lg" onClose={() => closeModal('end-lease')}>
      <ModalHeader
        title="End Lease"
        subtitle={lease ? `${lease.id} · ${lease.customer} · ${vehicle?.name || ''}` : undefined}
        onClose={() => closeModal('end-lease')}
      />
      <ModalBody>
        {!lease ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--tx3)' }}>
            No active lease found for this vehicle.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Step header */}
            <div style={{ display: 'flex', gap: 4, padding: '0 0 12px', borderBottom: '1px solid var(--bd)' }}>
              {STEPS.map((label, i) => {
                const n = i + 1;
                const active = step === n;
                const done = step > n;
                return (
                  <div key={n} style={{
                    flex: 1, textAlign: 'center', fontSize: 11, fontWeight: 600,
                    padding: '8px 6px', borderRadius: 'var(--r)',
                    background: active ? 'var(--ac-l)' : done ? 'var(--gr-l)' : 'transparent',
                    color: active ? 'var(--ac)' : done ? 'var(--gr)' : 'var(--tx3)',
                    border: `1px solid ${active ? 'var(--ac-b)' : done ? 'var(--gr-b)' : 'transparent'}`,
                  }}>
                    {done ? '✓ ' : `${n}. `}{label}
                  </div>
                );
              })}
            </div>

            {/* Lease summary banner */}
            <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 12px', fontSize: 11 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Lease</div>
                  <div style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>{lease.id}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Period</div>
                  <div style={{ fontWeight: 600 }}>{lease.startLabel} → {lease.endLabel}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Monthly</div>
                  <div style={{ fontWeight: 700, color: 'var(--gr)' }}>€{lease.monthlyRate}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Deposit held</div>
                  <div style={{ fontWeight: 700, color: 'var(--bl)' }}>€{(lease.deposit || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* ═══ STEP 1: Return Inspection ═══════════════════════════ */}
            {step === 1 && <>

              {/* Side-by-side: Annex A handover snapshot vs Now */}
              {(lease.condPre || (lease.photosPre && lease.photosPre.length > 0)) && (
                <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)', padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--bl)', marginBottom: 6 }}>
                    📋 Annex A — handover condition
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx2)', marginBottom: 6 }}>
                    {COND_OPTIONS.find(o => o.value === lease.condPre)?.label || lease.condPre}
                    {lease.condPreNote && <span style={{ color: 'var(--tx3)' }}> — {lease.condPreNote}</span>}
                  </div>
                  {Array.isArray(lease.photosPre) && lease.photosPre.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {lease.photosPre.map((src, i) => (
                        <img key={i} src={src} alt="handover"
                             style={{ width: 60, height: 45, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--bd)', cursor: 'pointer' }}
                             onClick={() => window.open(src, '_blank')} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Odometer + fuel */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Return odometer (km) *</label>
                  <input type="number" className="fi" value={returnKm}
                         onChange={e => setReturnKm(e.target.value)}
                         placeholder={vehicle?.odometer ? String(vehicle.odometer) : 'e.g. 145000'} />
                  {calc && calc.startKm > 0 && (
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 3 }}>
                      Start: {calc.startKm.toLocaleString()} km · Driven: <strong>{calc.kmDriven.toLocaleString()} km</strong>
                    </div>
                  )}
                </div>
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Fuel level on return</label>
                  <select className="fi" value={fuel} onChange={e => setFuel(e.target.value)}>
                    {FUEL_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Condition */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Return Condition</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {COND_OPTIONS.map(opt => (
                    <div key={opt.value} onClick={() => setCond(opt.value)} style={{
                      padding: '8px 12px', border: `1.5px solid ${cond === opt.value ? 'var(--ac)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11,
                      background: cond === opt.value ? 'var(--ac-l)' : 'var(--sur)',
                    }}>{opt.label}</div>
                  ))}
                </div>
              </div>

              <div className="fg" style={{ margin: 0 }}>
                <label className="fl">Condition notes (against Annex A)</label>
                <textarea className="fi" rows={2} value={condNote}
                          onChange={e => setCondNote(e.target.value)}
                          placeholder="Note any new damage, wear or items returned/missing..." />
              </div>

              {/* Damage records — log new + show existing linked to this lease */}
              {(() => {
                const existingDamages = getDamagesForRental ? (getDamagesForRental(lease.id) || []) : [];
                const condTriggers = cond === 'small_scratch' || cond === 'damage' || cond === 'major_damage';
                if (!condTriggers && existingDamages.length === 0) return null;
                const presetSeverity = cond === 'major_damage' ? 'major'
                                     : cond === 'damage'       ? 'moderate'
                                     : 'minor';
                const headline = existingDamages.length > 0
                  ? <>📋 <strong>{existingDamages.length} damage record{existingDamages.length === 1 ? '' : 's'}</strong> linked to this lease</>
                  : cond === 'small_scratch'
                    ? <>🔸 <strong>Small scratch / scuff</strong> — log it for the record (even if you waive the charge).</>
                    : cond === 'major_damage'
                      ? <>🆘 <strong>Major damage / accident</strong> — log details, photos and consider insurance claim.</>
                      : <>⚠️ <strong>Damage reported</strong> — log it as a structured record so you can charge / dispute / claim later.</>;
                return (
                  <div style={{ background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 'var(--r)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--ac)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div>{headline}</div>
                      <button
                        type="button" className="btn btn-primary btn-sm"
                        onClick={() => {
                          const cust = customers.find(c => `${c.fname} ${c.lname}`.trim() === (lease.customer || '').trim());
                          openModal('damage', {
                            vid: lease.vid, rentalId: lease.id,
                            customerId: cust?.id || null,
                            customerName: lease.customer || '',
                            description: condNote || '',
                            severity: presetSeverity,
                            paidBy: cond === 'small_scratch' ? 'absorbed' : 'unpaid',
                            status:  cond === 'small_scratch' ? 'dismissed' : 'reported',
                          });
                        }}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        + Damage Report
                      </button>
                    </div>
                    {existingDamages.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {existingDamages.map(d => (
                          <button
                            key={d.id} type="button"
                            onClick={() => openModal('damage', d.id)}
                            style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, cursor: 'pointer', textAlign: 'left' }}
                          >
                            <span>{d.location || 'Damage'} — {d.description?.slice(0, 50)}{d.description?.length > 50 ? '…' : ''}</span>
                            <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(d.finalCost ?? d.estimatedCost ?? 0).toFixed(0)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Photos */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
                  Return Photos
                  <span style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 400, marginLeft: 8 }}>
                    {Object.values(photos).filter(Boolean).length}/{PHOTO_SLOTS.length} added
                  </span>
                </div>
                <input ref={slotFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onSlotFile} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                  {PHOTO_SLOTS.map(label => (
                    <div key={label} style={{
                      border: `2px ${photos[label] ? 'solid var(--gr-b)' : 'dashed var(--bd)'}`,
                      borderRadius: 'var(--r)', overflow: 'hidden',
                      background: photos[label] ? 'transparent' : 'var(--sur2)',
                    }}>
                      {photos[label] ? (
                        <>
                          <img src={photos[label]} alt={label}
                               style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                               onClick={() => window.open(photos[label], '_blank')} />
                          <div style={{ padding: '4px 4px 3px', display: 'flex', gap: 3, justifyContent: 'center' }}>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotFile(label)}>📁</button>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotCamera(label)}>📷</button>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9, color: 'var(--ac)' }} onClick={() => removeSlotPhoto(label)}>✕</button>
                          </div>
                          <div style={{ textAlign: 'center', fontSize: 9, color: 'var(--gr)', paddingBottom: 4, fontWeight: 600 }}>✓ {label}</div>
                        </>
                      ) : (
                        <div style={{ padding: '16px 8px', textAlign: 'center', cursor: 'pointer', fontSize: 10, color: 'var(--tx3)' }}
                             onClick={() => openSlotFile(label)}>
                          <div style={{ fontSize: 20, marginBottom: 4 }}>📷</div>
                          <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 9 }}>+ Add</div>
                          <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: 9, padding: '2px 8px' }}
                                  onClick={e => { e.stopPropagation(); openSlotCamera(label); }}>📸 Camera</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>}

            {/* ═══ STEP 2: Charges ═════════════════════════════════════ */}
            {step === 2 && calc && <>

              {/* Excess km */}
              <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>📊 Excess Mileage</div>
                {calc.annualCap === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--gr)' }}>
                    🟢 Unlimited mileage — no excess charge.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 8, fontSize: 11 }}>
                      <div>
                        <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Cap (annual)</div>
                        <div style={{ fontWeight: 600 }}>{calc.annualCap.toLocaleString()} km</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Months elapsed</div>
                        <div style={{ fontWeight: 600 }}>{calc.monthsElapsed.toFixed(1)} mo</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Pro-rated allowed</div>
                        <div style={{ fontWeight: 600 }}>{calc.allowedKm?.toLocaleString() || '∞'} km</div>
                      </div>
                      <div>
                        <div style={{ color: 'var(--tx3)', fontSize: 10 }}>Actual driven</div>
                        <div style={{ fontWeight: 600 }}>{calc.kmDriven.toLocaleString()} km</div>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div className="fg" style={{ margin: 0 }}>
                        <label className="fl">Excess km rate (€/km)</label>
                        <input type="number" className="fi" min="0" step="0.01" value={excessKmRate}
                               onChange={e => setExcessKmRate(e.target.value)} />
                      </div>
                      <div className="fg" style={{ margin: 0 }}>
                        <label className="fl">Excess charge</label>
                        <div className="fi" style={{
                          background: calc.excessKm > 0 ? 'var(--am-l)' : 'var(--gr-l)',
                          color: calc.excessKm > 0 ? 'var(--am)' : 'var(--gr)',
                          fontWeight: 700, fontFamily: "'DM Mono',monospace",
                        }}>
                          {calc.excessKm > 0
                            ? `${calc.excessKm.toLocaleString()} km × €${excessKmRate} = €${calc.excessKmCharge.toFixed(2)}`
                            : '✓ Within allowance'}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Damage */}
              <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>🔧 Damage Charge</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Charge (€)</label>
                    <input type="number" className="fi" min="0" step="0.01" value={damageFee}
                           onChange={e => setDamageFee(e.target.value)} placeholder="0" />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Reason / description</label>
                    <input type="text" className="fi" value={damageNote}
                           onChange={e => setDamageNote(e.target.value)}
                           placeholder="e.g. Bumper scratch, interior cleaning..." />
                  </div>
                </div>
                {getDamagesForRental && (() => {
                  const dmgs = getDamagesForRental(lease.id) || [];
                  if (dmgs.length === 0) return null;
                  const total = dmgs.reduce((s, d) => s + (d.finalCost ?? d.estimatedCost ?? 0), 0);
                  return (
                    <div style={{ marginTop: 8, fontSize: 10, color: 'var(--tx3)' }}>
                      📋 {dmgs.length} damage record{dmgs.length === 1 ? '' : 's'} linked to this lease — total estimated €{total.toFixed(2)}
                    </div>
                  );
                })()}
              </div>

              {/* Late fee */}
              <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>⏱ Late Return Fee</div>
                {calc.daysLate === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--gr)' }}>
                    🟢 Returned on or before lease end — no late fee.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--am)', marginBottom: 8 }}>
                      ⚠️ <strong>{calc.daysLate} day{calc.daysLate === 1 ? '' : 's'} past lease end</strong> (ended {lease.endLabel})
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <div className="fg" style={{ margin: 0 }}>
                        <label className="fl">Daily rate (€)</label>
                        <input type="number" className="fi" min="0" step="0.01" value={lateFeePerDay}
                               onChange={e => setLateFeePerDay(e.target.value)} />
                      </div>
                      <div className="fg" style={{ margin: 0 }}>
                        <label className="fl">Auto-calc</label>
                        <div className="fi" style={{ background: 'var(--sur2)', color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>
                          €{calc.autoLateFee.toFixed(2)}
                        </div>
                      </div>
                      <div className="fg" style={{ margin: 0 }}>
                        <label className="fl">Override (waive/adjust)</label>
                        <input type="number" className="fi" min="0" step="0.01" value={lateFeeOverride}
                               onChange={e => setLateFeeOverride(e.target.value)}
                               placeholder={`leave blank = ${calc.autoLateFee.toFixed(2)}`} />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Deposit handling */}
              <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
                  💰 Deposit (held: €{calc.deposit.toFixed(2)})
                </div>
                {calc.deposit === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--tx3)' }}>No deposit on file for this lease.</div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {[
                        ['auto',    `🤖 Auto-settle — apply against charges, refund remainder${calc.totalCharges < calc.deposit ? ` (refund €${(calc.deposit - calc.totalCharges).toFixed(2)})` : ''}`],
                        ['full',    '✅ Full refund — charges billed separately'],
                        ['partial', '🔸 Partial refund'],
                        ['none',    '❌ No refund — apply 100% to charges'],
                      ].map(([val, label]) => (
                        <div key={val} onClick={() => setDepositRefund(val)} style={{
                          padding: '8px 12px', border: `1.5px solid ${depositRefund === val ? 'var(--ac)' : 'var(--bd)'}`,
                          borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11,
                          background: depositRefund === val ? 'var(--ac-l)' : 'var(--sur)',
                        }}>{label}</div>
                      ))}
                    </div>
                    {depositRefund === 'partial' && (
                      <div className="fg" style={{ marginTop: 8 }}>
                        <label className="fl">Refund amount (€)</label>
                        <input type="number" className="fi" value={depositPartialAmt}
                               onChange={e => setDepositPartialAmt(e.target.value)}
                               placeholder={`Max €${calc.deposit.toFixed(2)}`} />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Live total */}
              <div style={{
                background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)',
                padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--bl)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    Settlement
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tx2)' }}>
                    Total charges €{calc.totalCharges.toFixed(2)} − Deposit applied €{calc.depositDeducted.toFixed(2)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {calc.finalDue > 0 ? (
                    <>
                      <div style={{ fontSize: 10, color: 'var(--am)' }}>Customer owes</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--am)', fontFamily: "'DM Mono',monospace" }}>
                        €{calc.finalDue.toFixed(2)}
                      </div>
                    </>
                  ) : calc.depositReturned > 0 ? (
                    <>
                      <div style={{ fontSize: 10, color: 'var(--gr)' }}>Refund due</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--gr)', fontFamily: "'DM Mono',monospace" }}>
                        €{calc.depositReturned.toFixed(2)}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx3)' }}>Settled in full</div>
                  )}
                </div>
              </div>
            </>}

            {/* ═══ STEP 3: Buy-out (Optional) ═══════════════════════════ */}
            {step === 3 && <>
              <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '14px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>🚗 Vehicle Buy-out (Optional)</div>
                <div style={{ fontSize: 11, color: 'var(--tx3)', marginBottom: 12 }}>
                  Some lease customers want to purchase the vehicle at end of term. If so, enter the agreed buy-out price below and it'll be added to the final pro forma. The vehicle stays on your fleet for ownership-transfer follow-up in the next release.
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  {[
                    [false, '⏭️ Skip — return vehicle'],
                    [true,  '💰 Yes — customer is buying'],
                  ].map(([val, label]) => (
                    <div key={String(val)} onClick={() => setBuyout(val)} style={{
                      flex: 1, padding: '10px 14px', textAlign: 'center', fontSize: 11, fontWeight: 600,
                      border: `1.5px solid ${buyout === val ? 'var(--ac)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)', cursor: 'pointer',
                      background: buyout === val ? 'var(--ac-l)' : 'var(--sur)',
                    }}>{label}</div>
                  ))}
                </div>
                {buyout && (
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Agreed buy-out price (€) *</label>
                    <input type="number" className="fi" value={buyoutPrice} min="0" step="0.01"
                           onChange={e => setBuyoutPrice(e.target.value)}
                           placeholder="e.g. 6500" />
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 4 }}>
                      💡 Title transfer, road-tax handover and de-registration to be completed manually outside the system for now.
                    </div>
                  </div>
                )}
              </div>
            </>}

            {/* ═══ STEP 4: Sign-off ═════════════════════════════════════ */}
            {step === 4 && calc && <>
              {/* Summary card */}
              <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '14px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>📋 Closeout Summary</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11 }}>
                  <Row label="Driven" value={`${calc.kmDriven.toLocaleString()} km`} />
                  {calc.annualCap > 0 && <Row label="Mileage" value={calc.excessKm > 0 ? `❌ ${calc.excessKm} km over` : '✓ within allowance'} />}
                  <Row label="Condition" value={COND_OPTIONS.find(o => o.value === cond)?.label.replace(/^[^ ]+ /, '') || ''} />
                  <Row label="Fuel level" value={FUEL_OPTIONS.find(f => f.value === fuel)?.label || ''} />
                  <div style={{ height: 1, background: 'var(--bd)', margin: '6px 0' }} />
                  {calc.excessKmCharge > 0 && <Row label={`Excess mileage (${calc.excessKm} km)`} value={`€${calc.excessKmCharge.toFixed(2)}`} />}
                  {calc.damageFeeAmt > 0   && <Row label={`Damage${damageNote ? ` (${damageNote})` : ''}`} value={`€${calc.damageFeeAmt.toFixed(2)}`} />}
                  {calc.lateFeeAmt > 0     && <Row label={`Late fee (${calc.daysLate} day${calc.daysLate === 1 ? '' : 's'})`} value={`€${calc.lateFeeAmt.toFixed(2)}`} />}
                  {calc.buyoutAmt > 0      && <Row label="Vehicle buy-out" value={`€${calc.buyoutAmt.toFixed(2)}`} />}
                  {calc.totalCharges === 0 && <Row label="Charges" value="None — clean return" />}
                  {calc.deposit > 0 && <Row label="Deposit held" value={`€${calc.deposit.toFixed(2)}`} />}
                  {calc.depositDeducted > 0 && <Row label="Deposit applied" value={`−€${calc.depositDeducted.toFixed(2)}`} />}
                  {calc.depositReturned > 0 && <Row label="Deposit refund due" value={`€${calc.depositReturned.toFixed(2)}`} highlight="gr" />}
                  <div style={{ height: 1, background: 'var(--bd)', margin: '6px 0' }} />
                  <Row
                    label={calc.finalDue > 0 ? 'FINAL AMOUNT DUE' : 'SETTLED'}
                    value={calc.finalDue > 0 ? `€${calc.finalDue.toFixed(2)}` : '€0.00'}
                    big highlight={calc.finalDue > 0 ? 'am' : 'gr'}
                  />
                </div>
              </div>

              {/* Issue invoice toggle */}
              {calc.finalDue > 0 && (
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 12px', background: 'var(--sur2)', border: '1px solid var(--bd)',
                  borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 11,
                }}>
                  <input type="checkbox" checked={issueFinalInvoice} onChange={e => setIssueFinalInvoice(e.target.checked)} />
                  <span>Auto-generate <strong>final settlement pro forma</strong> for €{calc.finalDue.toFixed(2)} (NET-15)</span>
                </label>
              )}

              {/* Preview + sign row */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowPreview(true)}>
                  📋 Preview closeout doc
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                  const { tmpl, ctx } = getCloseoutContext();
                  if (tmpl) printContract(tmpl, ctx);
                  else showToast('No template available');
                }}>
                  🖨️ Print {signatureDataUrl ? '(signed)' : '(blank)'}
                </button>
              </div>

              {/* Signature pad */}
              <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600 }}>Customer sign-off</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                      {signatureDataUrl
                        ? <>✅ Signed on {signedAt ? new Date(signedAt).toLocaleString('en-GB') : 'now'}</>
                        : 'Optional — confirms acceptance of return condition & charges'}
                    </div>
                  </div>
                  {!signatureDataUrl ? (
                    <button type="button" className="btn btn-primary btn-sm"
                            onClick={() => {
                              setShowSig(true);
                              setTimeout(() => {
                                const c = sigCanvasRef.current;
                                if (c) {
                                  const ctx = c.getContext('2d');
                                  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
                                  ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round';
                                }
                              }, 50);
                            }}>
                      ✍️ Capture signature
                    </button>
                  ) : (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSignatureDataUrl(''); setSignedAt(''); }}>Clear</button>
                  )}
                </div>

                {signatureDataUrl && !showSig && (
                  <img src={signatureDataUrl} alt="signature"
                       style={{ maxWidth: 280, height: 'auto', background: '#fff', border: '1px solid var(--bd)', borderRadius: 4, display: 'block' }} />
                )}

                {showSig && (
                  <div>
                    <div style={{ background: '#fff', border: '2px dashed var(--bd2)', borderRadius: 6, display: 'inline-block' }}>
                      <canvas
                        ref={sigCanvasRef} width={420} height={140}
                        style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
                        onMouseDown={(e) => {
                          drawingRef.current = true;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const ctx = e.currentTarget.getContext('2d');
                          ctx.beginPath(); ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
                        }}
                        onMouseMove={(e) => {
                          if (!drawingRef.current) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const ctx = e.currentTarget.getContext('2d');
                          ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top); ctx.stroke();
                        }}
                        onMouseUp={() => { drawingRef.current = false; }}
                        onMouseLeave={() => { drawingRef.current = false; }}
                        onTouchStart={(e) => {
                          drawingRef.current = true;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const t = e.touches[0];
                          const ctx = e.currentTarget.getContext('2d');
                          ctx.beginPath(); ctx.moveTo(t.clientX - rect.left, t.clientY - rect.top);
                        }}
                        onTouchMove={(e) => {
                          if (!drawingRef.current) return;
                          e.preventDefault();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const t = e.touches[0];
                          const ctx = e.currentTarget.getContext('2d');
                          ctx.lineTo(t.clientX - rect.left, t.clientY - rect.top); ctx.stroke();
                        }}
                        onTouchEnd={() => { drawingRef.current = false; }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button type="button" className="btn btn-ghost btn-sm"
                              onClick={() => {
                                const c = sigCanvasRef.current;
                                if (!c) return;
                                const ctx = c.getContext('2d');
                                ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
                              }}>Erase</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowSig(false)}>Cancel</button>
                      <button type="button" className="btn btn-primary btn-sm"
                              onClick={() => {
                                const c = sigCanvasRef.current;
                                if (!c) return;
                                setSignatureDataUrl(c.toDataURL('image/png'));
                                setSignedAt(new Date().toISOString());
                                setShowSig(false);
                              }}>✓ Save signature</button>
                    </div>
                  </div>
                )}
              </div>
            </>}

          </div>
        )}

        {/* Camera overlay */}
        {camera && (
          <CameraCapture
            label={camera.label}
            onCapture={data => { camera.setPhoto(data); setCamera(null); }}
            onClose={() => setCamera(null)}
          />
        )}

        {/* Closeout doc preview overlay */}
        {showPreview && createPortal(
          <div onClick={() => setShowPreview(false)}
               style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()}
                 style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--bd)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  📋 Closeout document {signatureDataUrl ? '· Signed' : '· Not signed yet'}
                </div>
                <button onClick={() => setShowPreview(false)}
                        style={{ fontSize: 22, cursor: 'pointer', border: 'none', background: 'none', color: 'var(--tx3)', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, background: 'var(--sur2)' }}>
                {(() => {
                  const { tmpl, ctx } = getCloseoutContext();
                  return tmpl
                    ? <ContractDocument template={tmpl} context={ctx} paperLook={true} />
                    : <div style={{ padding: 30, color: 'var(--tx3)', textAlign: 'center' }}>No template available.</div>;
                })()}
              </div>
              <div style={{ padding: '12px 18px', borderTop: '1px solid var(--bd)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-ghost" onClick={() => setShowPreview(false)}>Close</button>
                <button className="btn btn-primary" onClick={() => {
                  const { tmpl, ctx } = getCloseoutContext();
                  if (tmpl) printContract(tmpl, ctx);
                }}>🖨️ Print / PDF</button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </ModalBody>
      <ModalFooter>
        <div style={{ fontSize: 10, color: 'var(--tx3)', marginRight: 'auto' }}>
          Step {step} of {STEPS.length} — {STEPS[step - 1]}
        </div>
        <button className="btn btn-ghost" onClick={() => closeModal('end-lease')}>Cancel</button>
        {step > 1 && <button className="btn btn-ghost" onClick={() => setStep(s => Math.max(1, s - 1))}>← Back</button>}
        {step < STEPS.length ? (
          <button className="btn btn-primary" onClick={() => {
            if (step === 1) {
              if (!returnKm) { showToast('Return odometer is required'); return; }
            }
            if (step === 3) {
              if (buyout && !buyoutPrice) { showToast('Enter buy-out price or pick Skip'); return; }
            }
            setStep(s => Math.min(STEPS.length, s + 1));
          }}>Next →</button>
        ) : (
          <button className="btn btn-primary" style={{ background: 'var(--ac)' }} onClick={submit} disabled={!lease}>
            ✓ Close Lease
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}

// Helper row for summary card
function Row({ label, value, big, highlight }) {
  const color = highlight === 'am' ? 'var(--am)'
              : highlight === 'gr' ? 'var(--gr)'
              : 'var(--tx1)';
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      fontSize: big ? 14 : 11,
      fontWeight: big ? 700 : 400,
      color: big ? color : 'var(--tx2)',
    }}>
      <span>{label}</span>
      <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600, color: highlight && !big ? color : undefined }}>{value}</span>
    </div>
  );
}
